import React, { useState, useEffect } from "react";
import {
    Plus, X, Package, Clock, CheckCircle2,
    ShoppingCart, AlertCircle, ChevronDown, Briefcase, RotateCw
} from "lucide-react";
import api from "../api/axiosInstance";

const MaterialRequestScreen = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ item_name: '', qty: '' });

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        if (selectedProject) fetchRequests();
    }, [selectedProject]);

    const fetchProjects = async () => {
        try {
            const res = await api.get("get_projects.php");
            const projectList = res.data.projects || [];
            setProjects(projectList);
            if (projectList.length > 0 && !selectedProject) {
                setSelectedProject(projectList[0]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_material_requests.php?project_id=${selectedProject.id}`);
            setRequests(res.data.requests || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleReload = async () => {
        setLoading(true);
        try {
            const resProjects = await api.get("get_projects.php");
            const projectList = resProjects.data.projects || [];
            setProjects(projectList);
            const activeProj = selectedProject || (projectList.length > 0 ? projectList[0] : null);
            if (activeProj) {
                if (!selectedProject) setSelectedProject(activeProj);
                const resRequests = await api.get(`get_material_requests.php?project_id=${activeProj.id}`);
                setRequests(resRequests.data.requests || []);
            }
        } catch (err) {
            console.error("Failed to reload data:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.item_name || !form.qty) return alert("Please fill all required inputs");
        try {
            await api.post("save_material_request.php", {
                ...form,
                project_id: selectedProject.id,
                date: new Date().toISOString().split("T")[0]
            });
            setModalVisible(false);
            setForm({ item_name: '', qty: '' });
            fetchRequests();
        } catch (err) {
            alert("Submission error");
        }
    };

    const getStatusDetails = (status) => {
        switch (status) {
            case 'Pending': return { bg: 'bg-amber-50 text-amber-600 border-amber-100', icon: <Clock size={14} /> };
            case 'Ordered': return { bg: 'bg-blue-50 text-blue-600 border-blue-100', icon: <ShoppingCart size={14} /> };
            case 'Delivered': return { bg: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: <CheckCircle2 size={14} /> };
            default: return { bg: 'bg-slate-50 text-slate-600 border-slate-100', icon: <AlertCircle size={14} /> };
        }
    };

    if (loading && projects.length === 0) return <div className="p-20 text-center font-black animate-pulse text-slate-400">CONNECTING TO PIPELINE...</div>;

    return (
        <div className="p-8 space-y-6 max-w-5xl mx-auto min-h-screen relative">
            {/* Selector and Action Block */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
                <div className="relative">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2">Active Site Horizon</label>
                    <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="flex items-center justify-between gap-4 min-w-[280px] bg-slate-50 border border-slate-100 px-5 py-3 rounded-xl font-bold text-slate-800 text-sm hover:bg-slate-100/70 transition-all text-left"
                    >
                        <span className="flex items-center gap-2">
                            <Briefcase size={16} className="text-indigo-500" />
                            {selectedProject ? selectedProject.project_name : "Select Active Hub"}
                        </span>
                        <ChevronDown size={16} className="text-slate-400" />
                    </button>

                    {dropdownOpen && (
                        <div className="absolute left-0 mt-2 w-full bg-white border border-slate-100 rounded-2xl shadow-xl z-50 overflow-hidden divide-y divide-slate-50 max-h-60 overflow-y-auto">
                            {projects.map((proj) => (
                                <button
                                    key={proj.id}
                                    onClick={() => {
                                        setSelectedProject(proj);
                                        setDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-5 py-3.5 text-xs font-black transition-all uppercase ${selectedProject?.id === proj.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"
                                        }`}
                                >
                                    {proj.project_name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3 self-end md:self-auto">
                    <button
                        onClick={handleReload}
                        disabled={loading}
                        title="Reload Material Requests Data"
                        className="bg-slate-50 hover:bg-slate-100 text-slate-700 px-5 py-3.5 rounded-2xl font-bold flex items-center gap-2 border border-slate-100 shadow-sm transition-all active:scale-95 disabled:opacity-60 text-xs uppercase tracking-wider"
                    >
                        <RotateCw size={16} className={`text-indigo-600 ${loading ? 'animate-spin' : ''}`} />
                        <span>{loading ? 'Reloading...' : 'Reload'}</span>
                    </button>
                    <button
                        onClick={() => setModalVisible(true)}
                        className="bg-slate-900 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl hover:bg-indigo-600 transition-all"
                    >
                        <Plus size={16} /> Request Material Dispatch
                    </button>
                </div>
            </div>

            {/* Requests Stream */}
            <div className="space-y-3">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-2">Requisition History Pipeline</p>
                {requests.map((item) => {
                    const badge = getStatusDetails(item.status);
                    return (
                        <div key={item.id} className="flex items-center justify-between p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-indigo-50/50 rounded-2xl flex items-center justify-center text-indigo-500">
                                    <Package size={20} />
                                </div>
                                <div>
                                    <h4 className="font-black text-slate-800 text-sm uppercase">{item.item_name}</h4>
                                    <p className="text-[11px] font-bold text-slate-400 uppercase mt-0.5">{item.qty} • Logged: {item.date}</p>
                                </div>
                            </div>
                            <div className={`flex items-center gap-1.5 px-4 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider ${badge.bg}`}>
                                {badge.icon} {item.status}
                            </div>
                        </div>
                    );
                })}
                {requests.length === 0 && (
                    <div className="text-center bg-white border border-dashed border-slate-200 rounded-[32px] p-16 text-slate-400 text-xs font-black uppercase tracking-widest">
                        No dynamic supply logs identified for this site location
                    </div>
                )}
            </div>

            {/* Entry Modal Overlay */}
            {modalVisible && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-[40px] shadow-2xl border border-slate-100 w-full max-w-lg p-8 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black italic uppercase tracking-tighter text-slate-900">Log Logistics Request</h3>
                            <button onClick={() => setModalVisible(false)} className="p-2 hover:bg-slate-100 rounded-full transition-all"><X size={20} className="text-slate-400" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Component Name & Spec</label>
                                <input
                                    type="text"
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-bold outline-none focus:ring-4 ring-indigo-500/5"
                                    placeholder="e.g. Century Plywood 19mm Commercial"
                                    value={form.item_name}
                                    onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Volume Quantity Required</label>
                                <input
                                    type="text"
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm font-bold outline-none focus:ring-4 ring-indigo-500/5"
                                    placeholder="e.g. 15 Sheets / 5 Boxes"
                                    value={form.qty}
                                    onChange={(e) => setForm({ ...form, qty: e.target.value })}
                                />
                            </div>
                            <button type="submit" className="w-full bg-indigo-600 text-white font-black text-xs uppercase tracking-widest py-4 rounded-2xl shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 transition-all pt-4">
                                Transmit Request To Office
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaterialRequestScreen;